import {
    Box,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChatIcon } from 'src/assets/svg/chat.svg';
import { ReactComponent as IconClose } from 'src/assets/svg/cross.svg';
import { ReactComponent as IconLink } from 'src/assets/svg/link.svg';

import { appConfig } from '../../app/app-config';
import { useContactModalStore } from '../rebooking/store';
import { CopyButton } from '../../components/copy-button';

import { WhatsappButton } from './components/whatsapp-button';
import { RequestCallback } from './components/request-callback-button';
import { ZendeskLiveChatButton } from './components/live-chat-button';
import { WechatButton } from './components/wechat-button';

export const GetInTouchModal: FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { open, closeModal } = useContactModalStore();

    const { palette } = theme;

    return (
        <Modal open={open} onClose={() => closeModal()} keepMounted={false}>
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Container maxWidth="md">
                    <Paper css={{ borderRadius: 0 }}>
                        <Stack
                            alignItems="stretch"
                            width="100%"
                            padding={{ xs: '32px 16px', md: '36px' }}
                            gap="32px"
                        >
                            <Stack direction="row" justifyContent="space-between">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap="16px"
                                    css={{
                                        textDecoration: 'none',
                                        color: palette.common.black,
                                    }}
                                >
                                    <ChatIcon />

                                    <Typography
                                        variant="h2"
                                        flex={1}
                                        css={{ textTransform: 'uppercase' }}
                                    >
                                        {t('contact_us')}
                                    </Typography>
                                </Stack>
                                <IconButton onClick={() => closeModal()}>
                                    <IconClose />
                                </IconButton>
                            </Stack>

                            <Box>
                                <Typography color={theme.palette.grey[700]}>
                                    {t('contact_us_directly')}
                                </Typography>
                            </Box>

                            <Grid
                                container
                                justifyContent="space-between"
                                columnSpacing={2}
                                rowGap={2}
                            >
                                <Grid item xs={12} md={12}>
                                    <ZendeskLiveChatButton />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <WhatsappButton />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <RequestCallback />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <WechatButton />
                                </Grid>
                            </Grid>

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color={theme.palette.grey[700]}>
                                    <strong css={{ color: theme.palette.primary.main }}>
                                        {`${t('contact_us_whatsapp')}:`}
                                    </strong>

                                    <br />

                                    {`${appConfig.contacts.whatsappPhoneNumber} `}
                                    <CopyButton text={appConfig.contacts.whatsappPhoneNumber} />
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color={theme.palette.grey[700]}
                                    css={{ textAlign: 'right' }}
                                >
                                    <strong css={{ color: theme.palette.primary.main }}>
                                        {`${t('contact_us_wechat')}:`}
                                    </strong>

                                    <br />

                                    {`${appConfig.contacts.weChatId} `}
                                    <CopyButton text={appConfig.contacts.weChatId} />
                                </Typography>
                            </Stack>

                            <Divider css={{ borderColor: theme.palette.primary.light }} />

                            <Box>
                                <Typography variant="h4" css={{ lineHeight: 2.2 }}>
                                    {t('contact_us_make_request_title')}
                                </Typography>

                                <Typography variant="body1">
                                    {`${t('contact_us_make_request_cta')} `}
                                    <Link
                                        href={appConfig.contacts.submitRequest}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('contact_us_make_request_cta_link')} <IconLink />
                                    </Link>
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Container>
            </Box>
        </Modal>
    );
};
